import { useMemo, useState, type FC } from "react";
import ChartLegend from "@/components/ChartLegend";
import ChartSwitcher from "@/components/ChartSwitcher";
import useDarkMode from "@/hooks/useDarkMode";
import {
  buildChannelBarOption,
  buildChannelDonutOption,
  buildChannelSlices,
  countChannels,
  CHANNEL_DONUT_INLINE_PX,
  CHANNEL_DONUT_STACKED_PX,
} from "@/pages/CampaignOverviewPage/charts/channelBreakdownChartOptions";
import { formatShare } from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import { pillBarChartHeightRem } from "@/pages/CampaignOverviewPage/charts/pillBarChart";
import ChartStateBody from "@/pages/CampaignOverviewPage/components/ChartStateBody";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/widgets/ChannelBreakdownWidget.module.css";

type ChannelChartType = "donut" | "horizontalBar";

const CHANNEL_CHART_OPTIONS: { key: ChannelChartType; label: string }[] = [
  { key: "donut", label: TEXT.campaignOverview.channelBreakdown.typeDonut },
  {
    key: "horizontalBar",
    label: TEXT.campaignOverview.channelBreakdown.typeHorizontalBar,
  },
];

// Inline (Row 2): 128px donut with its legend alongside.
// Stacked (no-video Row 3): 220px donut with its legend below.
const DONUT_STYLES = {
  inline: { flexShrink: 0, height: "8rem", width: "8rem" },
  stacked: { height: "13.75rem", width: "13.75rem" },
} as const;

interface ChannelBreakdownWidgetProps {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
  layout?: "inline" | "stacked";
}

const ChannelBreakdownWidget: FC<ChannelBreakdownWidgetProps> = ({
  error,
  isLoading,
  rows,
  layout = "inline",
}) => {
  const isDark = useDarkMode();
  const [chartType, setChartType] = useState<ChannelChartType>("donut");

  const option = useMemo(
    () =>
      chartType === "donut"
        ? buildChannelDonutOption(
            rows,
            isDark,
            layout === "stacked"
              ? CHANNEL_DONUT_STACKED_PX
              : CHANNEL_DONUT_INLINE_PX
          )
        : buildChannelBarOption(rows, isDark),
    [rows, chartType, isDark, layout]
  );

  const slices = buildChannelSlices(rows, isDark);
  const isDonut = chartType === "donut";

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>
          {TEXT.campaignOverview.channelBreakdown.title}
        </span>
        <ChartSwitcher
          label={TEXT.campaignOverview.switcher.chartType}
          options={CHANNEL_CHART_OPTIONS}
          value={chartType}
          onChange={setChartType}
        />
      </div>
      <div
        className={
          isDonut
            ? `${styles.donutBody} ${layout === "stacked" ? styles.stacked : ""}`
            : styles.body
        }
      >
        <ChartStateBody
          error={error}
          errorMessage={TEXT.campaignOverview.channelBreakdown.error}
          isEmpty={rows.length === 0}
          isLoading={isLoading}
          loadingClassName={styles.loading}
          messageClassName={styles.message}
          noDataMessage={TEXT.campaignOverview.channelBreakdown.noData}
          option={option}
          style={
            isDonut
              ? DONUT_STYLES[layout]
              : {
                  height: pillBarChartHeightRem(countChannels(rows)),
                  width: "100%",
                }
          }
        />
        {isDonut && !isLoading && !error && slices.length > 0 && (
          <ChartLegend
            items={slices.map(({ color, name, share }) => ({
              color,
              label: name,
              value: formatShare(share),
            }))}
          />
        )}
      </div>
    </div>
  );
};

export default ChannelBreakdownWidget;
